var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Utils, PCSS, Transformer } from '@pacem/pacem-core';
export function getComputeInterestsFormMetadata(from, until) {
    const retval = {
        display: { cssClass: [PCSS + '-grid', 'form-minimal'] },
        props: [
            {
                prop: 'from', type: 'date', display: {
                    cssClass: [PCSS + '-cell', 'cols-lg-4'], ui: 'calendar', name: 'from',
                    description: 'Start of interest computation period.'
                },
                extra: {
                    tooltip: true,
                    format: { year: 'numeric', month: 'short', day: '2-digit' }
                },
                validators: [
                    { type: 'required', errorMessage: 'Mandatory field.' }
                ]
            },
            {
                prop: 'to', type: 'date', display: {
                    cssClass: [PCSS + '-cell', 'cols-lg-4'], ui: 'calendar', name: 'end of contract',
                    description: 'End of contract date  \n(might be today if the contract is still active).'
                },
                extra: {
                    tooltip: 'md',
                    disabledRanges: [{ to: Utils.Dates.parse(from) }],
                    format: { year: 'numeric', month: 'short', day: '2-digit' }
                },
                validators: [
                    { type: 'required', errorMessage: 'Mandatory field.' }
                ]
            },
            {
                prop: 'until', type: 'date', display: {
                    cssClass: [PCSS + '-cell', 'cols-lg-4'], ui: 'calendar',
                    name: 'end of computation', description: 'Date to consider to stop the interest computation  \n(e.g. today if still in progress).'
                },
                extra: {
                    tooltip: 'md',
                    disabledRanges: [{ to: Utils.Dates.parse(from) }],
                    format: { year: 'numeric', month: 'short', day: '2-digit' }
                },
                validators: [
                    { type: 'required', errorMessage: 'Mandatory field.' }
                ]
            },
            {
                prop: 'monthlyAmount', type: 'number', display: {
                    cssClass: [PCSS + '-cell', 'cols-lg-6', 'cols-xl-4', 'field-stretch'],
                    name: 'monthly amount'
                },
                extra: { step: 10 },
                validators: [
                    { type: 'required', errorMessage: 'Mandatory field.' },
                    { type: 'range', errorMessage: 'Out of range', params: { min: 0 } }
                ]
            },
            {
                prop: 'paymentDate', type: 'number', display: {
                    cssClass: [PCSS + '-cell', 'cols-lg-6', 'cols-xl-4', 'field-stretch'],
                    name: 'monthly payment date'
                },
                extra: { step: 1 },
                validators: [
                    { type: 'required', errorMessage: 'Mandatory field.' },
                    { type: 'range', errorMessage: 'Out of range', params: { min: 1, max: 28 } }
                ]
            },
            {
                prop: 'initialAmount', type: 'number', display: {
                    cssClass: [PCSS + '-cell', 'cols-xl-4', 'field-stretch'],
                    name: 'initial amount'
                },
                extra: { step: 1 },
                validators: [
                    { type: 'range', errorMessage: 'Out of range', params: { min: 0 } }
                ]
            }
        ]
    };
    if (!Utils.isNullOrEmpty(from) && !Utils.isNullOrEmpty(until)) {
        const startYear = Utils.Dates.parse(from).getFullYear(), endYear = Utils.Dates.parse(until).getFullYear();
        if (startYear <= endYear) {
            let cursor = startYear;
            while (cursor <= endYear) {
                retval.props.push({
                    prop: 'year' + cursor, type: 'number', display: {
                        name: `Year ${cursor} interest rate`, cssClass: [PCSS + '-cell', 'cols-lg-4', 'cols-xl-3']
                    },
                    extra: { step: 0.0001 },
                    validators: [
                        { type: 'required', errorMessage: 'Mandatory field.' },
                        { type: 'range', errorMessage: 'Out of range', params: { min: 0 } }
                    ]
                });
                cursor++;
            }
        }
    }
    return retval;
}
function computeInterests(v0, monthlyAmount, from, to, until, rates, paymentDate = 1) {
    const start = Utils.Dates.parse(from), finish = Utils.Dates.parse(to), end = Utils.Dates.parse(until);
    const retval = { amount: 0, years: [] };
    let cursor = start;
    let accumulated = v0;
    do {
        const cursorYear = cursor.getFullYear();
        const isLeapYear = Utils.Dates.isLeapYear(cursorYear);
        const yearlyRate = rates.find(i => i.year === cursorYear);
        if (Utils.isNullOrEmpty(yearlyRate)) {
            return null;
        }
        const dailyRate = (yearlyRate.rate / (isLeapYear ? 366 : 365)).roundoff();
        const cursorMonth = cursor.getMonth();
        const daysInMonth = Utils.Dates.daysInMonth(cursorYear, cursorMonth);
        const cursorMonthEnd = new Date(cursorYear, cursorMonth, daysInMonth);
        const cursorMonthStart = new Date(cursorYear, cursorMonth, Math.max(paymentDate, cursor.getDate()));
        const endStep = Utils.Dates.parse(Math.min(end.valueOf(), cursorMonthEnd.valueOf()));
        const endOngoingStep = Utils.Dates.parse(Math.min(finish.valueOf(), cursorMonthEnd.valueOf()));
        const fullDays = Math.round(Utils.Dates.daysBetween(cursor, endStep)) + 1;
        const monthDays = Math.round(Utils.Dates.daysBetween(cursorMonthStart, endOngoingStep)) + 1;
        const accumulatedInterests = accumulated * dailyRate * fullDays;
        const monthlyInterests = monthDays > 0 ? (monthlyAmount * dailyRate * monthDays) : 0;
        const increment = (accumulatedInterests + monthlyInterests).roundoff();
        retval.amount += increment;
        let year = retval.years.find(i => i.year === cursorYear);
        if (Utils.isNull(year)) {
            year = { year: cursorYear, amount: 0, months: 0, days: 0, rate: yearlyRate.rate };
            retval.years.push(year);
        }
        year.amount += increment;
        year.days += fullDays;
        year.months++;
        if (endStep >= end) {
            break;
        }
        accumulated += monthlyAmount;
        const nextMonth = Utils.Dates.addMonths(endStep, 1);
        cursor = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
    } while (true);
    return retval;
}
class EconomicsTransformers {
    static computeInterestsInternal(monthlyAmount, from, to, until, rates, v0 = 0, paymentDate = 1) {
        if (typeof monthlyAmount === 'object') {
            const obj = monthlyAmount;
            const rates = [];
            for (let prop in obj) {
                if (prop.startsWith('year')) {
                    const yr = parseInt(prop.substr(4));
                    rates.push({ year: yr, rate: obj[prop] });
                }
            }
            return computeInterests(obj.initialAmount, obj.monthlyAmount, obj.from, obj.to, obj.until, rates, obj.paymentDate);
        }
        return computeInterests(v0, monthlyAmount, from, to, until, rates, paymentDate);
    }
}
__decorate([
    Transformer('computeInterests')
], EconomicsTransformers, "computeInterestsInternal", null);
